/**
 * Client Portal copy. Spanish only — en/pt fall back to es.
 * No native translation exists yet; see SPECS.lang.
 *
 * OUW-109: copy matches the 2026-09 artifact (tú). Do not invent variants
 * and do not restore usted. Source:
 * https://claude.ai/design/p/a62e5be2-67e1-45eb-a82d-93f57693486d?file=Portal%20del%20Cliente.dc.html
 */
export type ClientPortalLanguage = 'es' | 'en' | 'pt';

const COPY = {
  es: {
    titleAccess: 'Portal del Cliente · Olé Life',
    titleRecover: 'Recuperar acceso · Portal del Cliente',
    titleBoard: 'Tu solicitud · Portal del Cliente',
    logoAlt: 'Olé Life',
    footerQuestions: '¿Preguntas?',
    footerLegal: '© 2026 Olé Life. Seguros de vida.',
    privacy: 'Aviso de privacidad',
    terms: 'Términos',
    accessibility: 'Accesibilidad',
    reportFraud: 'Reportar fraude',
    yourAgent: 'tu agente',
    writeToAgent: 'Escribir a {name}',
    emailAgent: 'Correo a {name}',

    brandEyebrow: 'Portal del Cliente de Olé Life',
    brandTitleLine1: 'Tu solicitud,',
    brandTitleLine2: 'todo en un solo lugar.',
    brandLede:
      'Sin cuenta ni contraseña. Confirma tu nombre y sigue justo donde lo dejó tu agente.',
    check1: 'Sigue tu trámite en tiempo real',
    check1small: 'Ve exactamente en qué etapa está tu póliza',
    check2: 'Envía documentos de forma segura',
    check2small: 'Una foto desde tu teléfono alcanza',
    check3: 'Firma los formularios en línea',
    check3small: 'Sin imprimir, sin escanear, sin fax',
    trust: 'Cifrado de 256 bits · Tu enlace privado vence en 7 días',
    formEyebrow: 'Acceso seguro',
    formTitle: 'Confirmemos que eres tú',
    formSub: 'Escribe tu nombre tal como aparece en tu solicitud.',
    refChip: 'Solicitud {ref} · Iniciada el {date}',
    alertMismatch: 'Ese nombre no coincide con nuestros registros.',
    alertMismatchSmall:
      'Te quedan 2 intentos. Revisa la ortografía o usa el nombre tal como aparece en tu identificación.',
    alertNotFound: 'No encontramos esa solicitud.',
    alertNotFoundSmall: 'Revisa el enlace o pide uno nuevo a tu agente.',
    alertLoadError: 'No pudimos cargar tu solicitud.',
    alertLoadErrorSmall: 'Intenta de nuevo en unos minutos. Si sigue igual, escribe a tu agente.',
    labelFirst: 'Nombre',
    labelLast: 'Apellido',
    labelDob: 'Fecha de nacimiento',
    placeholderFirst: 'Maria',
    placeholderLast: 'González',
    placeholderDob: 'MM / DD / AAAA',
    dobHint: 'Solo la usamos para verificar tu identidad.',
    continue: 'Ver mi solicitud',
    cantAccess: 'No puedo entrar',
    expiredEyebrow: 'Enlace vencido',
    expiredTitle: 'Este enlace ya venció',
    expiredBody:
      'Por tu seguridad, los enlaces del portal vencen a los 7 días. Podemos enviarte uno nuevo al correo y al teléfono que ya tenemos registrados en tu solicitud.',
    resend: 'Enviarme un enlace nuevo',
    lockedEyebrow: 'Acceso pausado',
    lockedTitle: 'Demasiados intentos',
    lockedBody:
      'Pausamos el acceso a esta solicitud por 30 minutos. Si necesitas ayuda antes, tu agente Daniel Ruiz puede verificar tu identidad por teléfono.',
    callAgent: 'Llamar a mi agente',
    okEyebrow: 'Verificado',
    okTitle: 'Bienvenida de nuevo, María',
    okBody:
      'Identidad confirmada. En la versión real esto lleva directo al resumen de tu solicitud; esta maqueta termina aquí.',
    okPill: 'Sigue: /portal/:token/overview',
    strip1Title: 'En qué va tu trámite',
    strip1Body: 'Una línea de tiempo clara, de enviada a aprobada, sin tecnicismos de seguros.',
    strip2Title: 'Envía lo que te pedimos',
    strip2Body: 'Toma una foto de tu identificación o tu recibo de pago. Te avisamos apenas la aceptemos.',
    strip3Title: 'Firma sin imprimir',
    strip3Body: 'Los formularios te llegan listos para firmar desde el teléfono, en español o en inglés.',

    helpBack: 'Volver al inicio',
    helpTitle: 'Recuperemos tu acceso',
    helpLead:
      'Tres formas de retomar el trámite. Elige la que te resulte más fácil: tu solicitud sigue exactamente donde la dejaste.',
    helpOpt1Title: 'Enviarme un enlace nuevo con mis datos de acceso',
    helpOpt1Body:
      'Te llega a {email} y al {phone}, junto con el número de tu solicitud.',
    helpOpt1Cta: 'Enviar enlace',
    helpOpt2Title: 'Escribir a soporte',
    helpOpt2Body:
      'Escríbenos a {email} y menciona la solicitud {ref}. Normalmente respondemos el mismo día hábil.',
    helpOpt2Cta: 'Enviar correo',
    helpOpt3Title: 'Hablar con tu agente',
    helpOpt3Body: 'Daniel ya tiene tu expediente abierto y puede verificar tu identidad en un par de minutos.',
    helpWa: 'WhatsApp',
    helpEmail: 'Email',
    helpSentTitle: 'Revisa tu correo',
    helpSentBody:
      'Si esta solicitud existe, te enviamos un enlace nuevo al correo y al teléfono registrados. Sirve por los próximos 7 días.',
    helpRetry: 'Probar de otra forma',

    boardHello: 'Hola, {name}',
    boardMeta: 'Solicitud {ref} · Vida a término, {term} · {coverage}',
    boardLoading: 'Cargando tu solicitud…',
    boardNotFound: 'No encontramos esa solicitud.',
    boardNotFoundBody: 'Revisa el enlace o vuelve al inicio para pedir uno nuevo a tu agente.',
    boardLoadError: 'No pudimos cargar tu solicitud.',
    initialLiveTitle: 'Estamos esperando documentos',
    initialLiveBody:
      'Tu solicitud todavía no entra a evaluación. Te avisamos cuando haya algo que enviar.',
    logout: 'Salir',
    stepSent: 'Solicitud enviada',
    stepSentSub: '24 de agosto',
    stepReqs: 'Requisitos básicos',
    stepReqsSub: 'Documentos iniciales',
    stepReview: 'En evaluación',
    stepReviewSub: 'Revisión médica',
    stepDecision: 'Decisión',
    stepDecisionSub: 'Respuesta final',
    summaryTitle: 'LO QUE SOLICITASTE',
    summaryCoverage: 'Cobertura',
    summaryTerm: 'Plazo',
    summaryPremium: 'Prima estimada',
    summaryAddOns: 'Coberturas adicionales',
    summaryHolder: 'Titular',
    summaryBeneficiaries: 'Beneficiarios',
    agentTitle: 'TU AGENTE',
    received: 'Recibido',
    signed: 'Firmada',
    answered: 'Respondido',
    upload: 'Subir',
    sign: 'Firmar',
    reply: 'Responder',
    uwNote: 'Nota del evaluador',
    // OUW-246: la tabla de primas del endoso. La nota de ASA le dice al cliente
    // que «el importe resultante se muestra en la columna Prima nueva» — hasta
    // ahora esa columna solo existía en el PDF.
    premiumTableTitle: 'Cómo queda tu prima',
    premiumTableCoverage: 'Cobertura',
    premiumTableBefore: 'Prima actual',
    premiumTableAfter: 'Prima nueva',
    premiumTableDiff: 'Diferencia',
    premiumTableBase: 'Fallecimiento',
    premiumTableRop: 'Devolución de prima',
    premiumTableNote: 'Importes por el período que pagas hoy.',

    initialEyebrow: 'Acción requerida',
    initialTitle: 'Faltan 3 cosas para empezar',
    initialBody:
      'Tu solicitud todavía no entra a evaluación. Apenas recibamos esto, pasa al equipo médico. Desde el teléfono te lleva unos minutos.',
    initialDeadline: 'Tienes hasta el 23 de septiembre para enviarlos',
    incomeTitle: 'Comprobante de ingresos',
    incomeBody: 'Recibido el 25 de agosto.',
    idTitle: 'Identificación con foto',
    idBody: 'Frente y dorso de tu licencia, pasaporte o matrícula consular.',
    hipaaTitle: 'Autorización HIPAA',
    hipaaBody: 'Nos permite pedir tu historial médico a tus doctores.',
    questTitle: 'Cuestionario de salud',
    questBody: '12 preguntas. Puedes guardar y seguir en otro momento.',

    reviewEyebrow: 'En evaluación',
    reviewTitle: 'Tu solicitud está con el equipo médico',
    reviewBody: 'No necesitamos nada de tu parte por ahora. Te avisamos apenas haya novedades.',
    reviewEta: 'Decisión estimada antes del 11 de septiembre',
    notifyCta: 'Avisarme por WhatsApp',
    activityTitle: 'Actividad',
    activity1: 'Asignada a evaluación médica',
    activity1Sub: '27 de agosto · en curso',
    activity2: 'Requisitos iniciales completos',
    activity2Sub: '26 de agosto',
    activity3: 'Solicitud enviada por Daniel Ruiz',
    activity3Sub: '24 de agosto',

    uwEyebrow: 'Acción requerida',
    uwTitle: 'El evaluador necesita 2 cosas más',
    uwBody: 'Tu solicitud está en pausa hasta que las recibamos. Cuanto antes las envíes, antes retoma el proceso.',
    uwDeadline: 'Tienes hasta el 29 de septiembre para enviarlas',
    labTitle: 'Resultados de laboratorio completos',
    labBody: 'Del examen del 12 de agosto.',
    labNote: 'Recibimos solo la primera página. Necesitamos el panel metabólico completo, con las dos hojas.',
    labDue: 'Vence en 6 días',
    medTitle: 'Aclaración sobre un medicamento',
    medBody: 'Sobre lo que declaraste en la pregunta 7 del cuestionario.',
    medNote: '¿Desde cuándo lo tomas y en qué dosis? Si tienes la receta a mano, adjunta una foto.',

    counterEyebrow: 'Nueva oferta',
    counterTitle: 'Podemos cubrirlo, con algunos cambios',
    counterBody:
      'Revisamos tu información y no podemos emitir la póliza exactamente como la pediste, pero sí esta versión. Nada cambia hasta que la aceptes.',
    counterExpires: 'Vence el 11 de septiembre',
    chgPremium: 'Prima',
    chgPremiumDelta: 'Sube 15%',
    chgWas: 'Pedía',
    chgNow: 'Nueva',
    chgNowM: 'Nuevo',
    premiumNote: 'El aumento refleja el perfil de riesgo por las actividades que declaraste en la pregunta 9.',
    chgCoverage: 'Monto asegurado',
    chgCoverageDelta: 'Baja $90,000',
    coverageNote: 'El monto que pediste supera lo que podemos emitir con los ingresos que pudimos comprobar.',
    chgRiders: 'Coberturas adicionales',
    chgRidersDelta: '2 de 4 se quitan',
    riderKeep: 'Se mantiene',
    riderAdd: 'Se agrega',
    riderChange: 'Se cambia',
    riderDrop: 'Se quita',
    rider1: 'Adelanto de efectivo',
    rider2: 'Enfermedades graves',
    rider3: 'Solo cáncer',
    rider4: 'Devolución de prima',
    ridersNote: 'Solo cáncer y Devolución de prima no están disponibles en esta clase de riesgo.',
    chgExclusions: 'Exclusiones nuevas',
    chgExclusionsDelta: '3 agregadas',
    excl1: 'Deportes de riesgo / aventura',
    excl2: 'Motociclismo',
    excl3: 'Buceo a más de 30 m',
    exclNote: 'Un fallecimiento causado por estas actividades no tendría cobertura. El resto de la póliza no cambia.',
    offerAttach: 'Detalle de exclusiones.pdf',
    acceptOffer: 'Aceptar la nueva oferta',
    viewOffer: 'Ver la oferta completa',
    // OUW-186: firmar la oferta ES aceptarla, en un solo acto.
    signOfferEyebrow: 'Último paso',
    signOfferTitle: 'Acepta y firma tu nueva oferta',
    signOfferBody: 'Vas a firmar electrónicamente la propuesta que te enviamos. Cuando termines, tu solicitud queda confirmada.',
    signOfferRejectWarning: 'Si rechazas la firma, la oferta se cierra y no vas a poder aceptarla más tarde. Si prefieres pensarlo, vuelve y hazlo cuando quieras: tienes tiempo hasta que la oferta venza.',
    signOfferCta: 'Aceptar y firmar',
    signOfferRedirecting: 'Abriendo la firma…',
    signOfferBack: 'Volver',
    signOfferError: 'No pudimos abrir la firma. Vuelve a intentarlo en unos minutos.',
    signOfferGone: 'Esta oferta ya no está disponible.',
    signOfferRegistering: 'Estamos registrando tu firma…',
    signOfferRegisteringNote: 'Puede demorar unos segundos. No cierres esta página; se actualiza sola.',
    // OUW-181: la propuesta se arma en segundo plano al emitirse la oferta.
    offerDocumentPreparing: 'Estamos preparando tu propuesta…',
    offerDocumentError: 'No pudimos abrir la propuesta. Volvé a intentarlo.',
    declineOffer: 'No aceptar',
    counterFoot:
      'Si no respondes antes del 11 de septiembre, la oferta se vence y la solicitud se cierra. No queda aceptada por defecto.',
    counterLoading: 'Cargando la oferta…',
    counterExpiresUntil: 'Tienes hasta el {date}',
    // Live: la fecha sale de offer.expires_at. El `counterFoot` de arriba es del
    // prototipo (fecha fija) y queda SOLO para el mock ?state=counter.
    counterFootUntil:
      'Si no respondes antes del {date}, la oferta se vence y la solicitud se cierra. No queda aceptada por defecto.',
    chgActual: 'Actual',
    chgProposed: 'Propuesto',
    chgDeltaUpPercent: 'Sube {n}%',
    chgDeltaDownPercent: 'Baja {n}%',
    chgDeltaUpFlat: 'Sube {n}',
    chgDeltaDownFlat: 'Baja {n}',
    acceptConfirm: 'Aceptar estas condiciones',
    offerWriteError: 'No pudimos registrar tu respuesta. Intenta de nuevo.',
    offerWriteBusy: 'Enviando…',

    confirmEyebrow: 'Aprobada',
    confirmTitle: 'Confirma para continuar',
    confirmBody:
      'Tu solicitud fue aprobada con las mismas condiciones. Confirma para seguir con el pago.',
    confirmNow: 'Confirmar',
    confirmWriteBusy: 'Confirmando…',
    confirmWriteError: 'No pudimos registrar tu confirmación. Intenta de nuevo.',
    confirmFoot:
      'Al confirmar, aceptas esta aprobación para continuar. El pago es el paso siguiente.',
    // OUW-70 (review): la confirmación SÍ se registró; lo que falló fue traer
    // el estado nuevo. Por eso no repite «intenta de nuevo» — reintentar acá
    // vuelve a pedir el estado, no a confirmar de nuevo.
    confirmRefreshError: 'Ya registramos tu confirmación. No pudimos actualizar la pantalla.',
    // OUW-108: 03E sin awaiting todavía — el CTA se ve, pero no aplica.
    confirmDisabledReason: 'Todavía no puedes confirmar',
    payDisabledReason: 'El link de pago aún no está listo',

    approvedEyebrow: 'APROBADA Y FIRMADA',
    approvedTitle: 'Solo falta el primer pago',
    approvedBody:
      'Tu póliza está aprobada y ya la firmaste. La cobertura entra en vigencia el día que recibamos el primer pago.',
    termCoverage: 'Cobertura',
    termFirstPay: 'Primer pago',
    termClass: 'Clase',
    termClassValue: 'Preferente no fumador',
    // OUW-200 (H4): el monto era un literal ($47.20) y no coincidía con la prima
    // aceptada que el mismo panel muestra como «Primer pago». Ahora se interpola.
    payNow: 'Pagar {amount} ahora',
    // OUW-67: el cliente volvió de la web de pagos antes que el aviso del
    // primer pago. No se le vuelve a ofrecer «Pagar» como botón principal.
    payConfirming: 'Estamos confirmando tu pago',
    payConfirmingTitle: 'Esperando la confirmación del pago',
    payConfirmingBody:
      'Ya registramos que saliste a pagar. No vuelvas a pagar. La cobertura entra en vigencia el día que se acredite el primer pago, y te avisamos por correo cuando la póliza quede en vigor. El estado puede tardar unos minutos en actualizarse, o algunos días si pagaste por transferencia.',
    payRefreshError: 'No pudimos actualizar el estado de tu pago.',
    payConfirmingFoot:
      'Si ya pagaste, no hace falta volver a hacerlo: puede tardar unos minutos en verse aquí, o algunos días si pagaste por transferencia. Si no terminaste el pago, puedes retomarlo abajo.',
    payResume: 'No terminé el pago',
    viewSignedPolicy: 'Ver la póliza firmada',
    approvedFoot:
      'Este pago también deja programado el débito {period}. Hasta que no lo recibamos, no hay cobertura.',

    deniedEyebrow: 'No aprobada',
    deniedTitle: 'No pudimos aprobar esta solicitud',
    deniedBody:
      'La decisión se tomó a partir de la información médica que recibimos. Te enviamos por correo una carta con el motivo específico y con tus derechos, incluido el de pedir una copia de la información que usamos.',
    viewLetter: 'Ver la carta',
    nextTitle: 'Qué puedes hacer ahora',
    nextBody:
      'Esta decisión es sobre este producto y con la información de hoy. {name} puede revisar contigo otras coberturas para las que sí podrías calificar.',
    alternatives: 'Ver otras opciones',

    // Copy LIVE (OUW-62). El de arriba es del prototipo y se queda solo en el
    // mock: trae fechas, un ETA y un agente que no existen en la API. Acá se
    // dice lo que se sabe y nada más.
    processingTitle: 'Solicitud en proceso',
    processingBody: 'Estamos revisando tu solicitud. Es cuestión de segundos.',
    // OUW-98: pasados unos minutos, «cuestión de segundos» deja de ser
    // cierto. No se promete un plazo nuevo: se dice que avisamos.
    processingSlowTitle: 'Está tardando más de lo normal',
    processingSlowBody:
      'Seguimos procesando tu solicitud. Te avisamos por correo apenas tengamos el resultado, no hace falta que esperes en esta pantalla.',
    counterLoadError:
      'No pudimos cargar los detalles de la nueva oferta. Vuelve a intentar en un momento o escríbenos.',
    retry: 'Reintentar',
    // PROVISIONAL (OUW-63). Copy de producto pendiente (Yamil / Lucas / Iris / Ana).
    reqMissingCount: 'Faltan {count}',
    reqAllSent: 'Todo enviado, en evaluación',
    reqLoadError: 'No pudimos cargar tus requisitos. Vuelve a intentar en un momento o escríbenos.',
    reqEmpty: 'Por ahora no hay requisitos para enviar. Si hace falta algo, te avisamos.',
    // OUW-260: el cliente propone horarios para la videollamada. El copy no
    // promete una cita: PROPONE. Quien agenda y manda el invite es el equipo
    // de suscripción, y prometer de más acá se lee como una confirmación.
    scheduleCta: 'Proponer horarios',
    scheduleCtaAgain: 'Cambiar horarios',
    scheduleEyebrow: 'Entrevista por videollamada',
    scheduleTitle: '¿Cuándo te viene bien?',
    scheduleLead:
      'Elegí hasta tres horarios que te sirvan. El equipo de suscripción confirma uno y te manda la invitación. La llamada dura una hora.',
    // OUW-336 (Lucas, 07-oct): el examen médico con Olé usa el mismo modal con
    // su propio título y bajada, y sin el encabezado de la videollamada. El
    // resto de los textos del modal no cambia (decisión del 07-oct).
    scheduleExamTitle: 'Reserva tu examen',
    scheduleExamLead:
      'Médico y de laboratorio. Te recomendamos la mañana, ya que algunos requieren ayuno.',
    scheduleDay: 'Día',
    scheduleHour: 'Hora',
    scheduleHourPlaceholder: 'Elegir',
    scheduleAdd: 'Agregar otro horario',
    scheduleRemove: 'Quitar este horario',
    scheduleNotePlaceholder: '¿Algo que debamos tener en cuenta? (opcional)',
    scheduleSend: 'Enviar horarios',
    scheduleSending: 'Enviando…',
    scheduleFoot: 'Horarios en hora de Ciudad de México, de lunes a viernes.',
    schedulePickDay: 'Elegir día',
    schedulePrevMonth: 'Mes anterior',
    scheduleNextMonth: 'Mes siguiente',
    scheduleWeekendsClosed: 'Fines de semana no disponibles',
    scheduleDoneTitle: 'Listo, recibimos tus horarios',
    scheduleDoneLead:
      'El equipo de suscripción confirma uno y te avisa por correo con el enlace de la videollamada.',
    scheduleErrorRepeated: 'Hay dos horarios iguales. Cambiá uno.',
    scheduleErrorInvalid:
      'Alguno de los horarios no sirve. Tienen que ser a partir de mañana, en punto o y media.',
    scheduleErrorSend: 'No pudimos guardar tus horarios. Volvé a intentar en un momento.',
    // Estados de la fila una vez que el cliente ya propuso.
    scheduleProposed: 'Enviaste tus horarios',
    scheduleProposedDetail: 'Te confirmamos uno por correo.',
    scheduleBooked: 'Entrevista confirmada',
    scheduleJoin: 'Entrar a la videollamada',
    reqInReview: 'En evaluación',
    reqOpen: 'Abrir',
    reqDownload: 'Descargar',
    reqMaxAgeDayOne: 'Vigencia máxima: 1 día.',
    reqMaxAgeDayMany: 'Vigencia máxima: {n} días.',
    reqMaxAgeMonthOne: 'Vigencia máxima: 1 mes.',
    reqMaxAgeMonthMany: 'Vigencia máxima: {n} meses.',
    reqMaxAgeYearOne: 'Vigencia máxima: 1 año.',
    reqMaxAgeYearMany: 'Vigencia máxima: {n} años.',
    reqRemoveFile: 'Quitar',
    reqPartLabel: 'Parte',
    reqFileTooBig: 'El archivo supera los 50 MB.',
    reqFileBadType: 'Usa PDF, JPG, PNG, WEBP o HEIC.',
    uploadHintLive: 'PDF, JPG, PNG, WEBP o HEIC. Hasta 50 MB.',
    uploadLeadLive: 'Puedes adjuntar más de un archivo. El requisito se entrega cuando pulses Enviar.',
    reviewLiveBody: 'No necesitamos nada de tu parte por ahora. Te avisamos apenas haya novedades.',
    deniedLiveBody:
      'La decisión se tomó a partir de la información que recibimos. Si quieres el detalle o una copia de la información que usamos, escríbenos y te la damos.',
    nextLiveBody:
      'Esta decisión es sobre este producto y con la información de hoy. Tu agente puede revisar contigo otras coberturas para las que sí podrías calificar.',

    activeEyebrow: 'Vigente',
    activeTitle: 'Tu póliza está activa',
    activeBody:
      'En vigencia desde el 2 de septiembre de 2026. Te mandamos la póliza y el comprobante por correo, y aquí los tienes siempre a mano.',
    // 03G live (OUW-67). The prototype body names a fixture date and documents
    // that the live GET does not serve yet (póliza/recibo = later).
    activeLiveBody: 'Tu cobertura ya está vigente. El primer pago quedó acreditado.',
    activeLiveBodySince: 'Tu cobertura está vigente desde el {date}. El primer pago quedó acreditado.',
    termMonthly: 'Prima mensual',
    // En vivo la frecuencia sale de la prima (M/Q/S/A), no se asume mensual.
    termPremium: 'Prima {period}',
    termNextDebit: 'Próximo débito',
    viewPolicy: 'Ver mi póliza',
    viewReceipt: 'Ver comprobante',
    changePayMethod: 'Cambiar forma de pago',
    activeFoot:
      'Si un débito falla, tienes 31 días de gracia antes de que la póliza caduque. Te avisamos por correo y WhatsApp el mismo día.',
    activeLiveFoot:
      'Si un débito falla, tienes 30 días de gracia antes de que la póliza caduque.',

    cancelledEyebrow: 'Cancelada',
    cancelledTitle: 'Esta solicitud está cerrada',
    cancelledBody:
      'Se cerró el 23 de septiembre porque no llegamos a recibir los documentos médicos dentro de los 30 días. No queda nada pendiente de tu parte.',
    cancelMotive: 'Motivo del cierre',
    cancelMotiveBody:
      'Vencieron los requisitos médicos iniciales. Faltaban: identificación con foto, autorización HIPAA y cuestionario de salud.',
    restart: 'Empezar una solicitud nueva',

    // 03H live (OUW-76). El copy de arriba es el del prototipo y se queda en el
    // mock; en vivo la fecha y el motivo salen de la API. `cancelMotiveOther` es
    // la red: la máquina hoy sella UN solo código de cancelación
    // (`offer_not_accepted`) — los cierres por reloj llegan sin código propio,
    // y un motivo desconocido no puede romper la pantalla.
    cancelledBodyRejected:
      'Se cerró el {date} porque no aceptaste la nueva oferta. No queda nada pendiente de tu parte.',
    cancelledBodyOn:
      'Se cerró el {date}. No queda nada pendiente de tu parte.',
    cancelledBodyPlain:
      'Esta solicitud ya está cerrada. No queda nada pendiente de tu parte.',
    cancelMotiveOfferNotAccepted: 'Rechazo de la oferta.',
    cancelMotiveExpired: 'Vencieron los plazos de la solicitud.',
    cancelMotiveOther: 'La solicitud se cerró.',
    cancelledFoot:
      'Nada de lo que ya nos enviaste se pierde: si vuelves a solicitar, {name} lo reutiliza y no tienes que cargarlo otra vez.',

    close: 'Cerrar',
    // OUW-276 (copy de producto, Lucía 06-oct): la primera visita después de firmar la solicitud.
    signedNoticeTitle: '¡Listo! Recibimos tu firma.',
    signedNoticeBody: 'Desde aquí podrás ver el estado de tu solicitud.',
    signedNoticeCta: 'Entendido',
    cancel: 'Cancelar',
    done: 'Listo',
    understood: 'Entendido',
    next: 'Siguiente',
    send: 'Enviar',
    uploadEyebrow: 'Subir documento',
    uploadLead: 'Puedes sacarle una foto con el teléfono. No hace falta escanear ni recortar.',
    uploadDrop: 'Arrastra el archivo aquí',
    uploadHint: 'JPG, PNG, HEIC o PDF · hasta 15 MB',
    takePhoto: 'Tomar foto',
    chooseFile: 'Elegir archivo',
    uploadingEyebrow: 'Subiendo',
    uploadingTitle: 'Un momento',
    uploadingLead: 'No cierres esta ventana hasta que termine.',
    uploadDoneTitle: 'Documento recibido',
    uploadDoneLead: 'Ya lo tiene el evaluador. Vas a ver el cambio en la lista.',
    signEyebrow: 'Firma electrónica',
    signTitle: 'Autorización HIPAA',
    signLead: 'Léela y fírmala aquí mismo. Te queda una copia en PDF.',
    signDoc:
      'Autorizo a Olé Life Insurance Services y a sus reaseguradores a obtener y usar mi información médica, incluyendo historia clínica, resultados de laboratorio y registros de farmacia, con el único fin de evaluar esta solicitud de seguro de vida.\n\nEsta autorización vence a los 24 meses de la fecha de firma. Puedo revocarla por escrito en cualquier momento, salvo respecto de la información que ya se haya usado antes de la revocación.\n\nEntiendo que tengo derecho a recibir una copia de esta autorización.',
    signCheck: 'Leí y autorizo a Olé Life a solicitar mi historial médico en los términos de arriba.',
    signNameLabel: 'Escribe tu nombre completo para firmar',
    signCta: 'Firmar',
    signDoneTitle: 'Autorización firmada',
    signDoneLead: 'Guardamos la firma con fecha, hora y el texto exacto. Te mandamos el PDF por correo.',
    questEyebrow: 'Cuestionario de salud',
    questStepTitle: 'Pregunta 1 de 12',
    questQ1: '¿Fumaste cigarrillos o usaste productos con nicotina en los últimos 12 meses?',
    questNo: 'No',
    questOcc: 'Sí, ocasionalmente',
    questDaily: 'Sí, todos los días',
    questSave: 'Guardar y seguir después',
    questDoneTitle: 'Cuestionario completo',
    questDoneLead:
      'En la maqueta salteamos las 11 preguntas que siguen. En el producto real, cada respuesta se guarda apenas se contesta.',
    replyEyebrow: 'Responder al evaluador',
    replyAsked: 'Lo que preguntó',
    replyPlaceholder: 'Por ejemplo: lo tomo desde marzo de 2025, 50 mg por día, recetado por el Dr. Peña.',
    replyAttach: 'Adjuntar foto de la receta',
    replyDoneTitle: 'Respuesta enviada',
    replyDoneLead: 'Vuelve directo a la cola del evaluador que la pidió, no al final de la fila.',
    acceptEyebrow: 'Confirmar',
    acceptTitle: 'Estás aceptando estas condiciones',
    acceptLead: 'No son las que pediste. Revísalas una vez más antes de seguir.',
    acceptPremium: 'Prima mensual',
    acceptCoverage: 'Monto asegurado',
    acceptRiders: 'Coberturas adicionales',
    acceptExclusions: 'Exclusiones nuevas',
    // En vivo el recuento sale de changes[] de la oferta. La maqueta usa
    // chgRidersDelta / chgExclusionsDelta, que traen números fijos.
    acceptRidersDropped: '{dropped} de {total} se quitan',
    acceptExclusionsAdded: '{count} agregadas',
    acceptBefore: 'antes',
    acceptCheck: 'Entiendo que estas condiciones reemplazan a las que había solicitado.',
    acceptBack: 'Volver',
    acceptContinue: 'Continuar a la firma',
    declineEyebrow: 'Antes de rechazar',
    declineTitle: '¿Seguro que no quieres esta oferta?',
    declineLead:
      'Si hay algo que no se entiende, {name} te lo explica en dos minutos. Rechazar cierra la solicitud y para volver hay que empezar de nuevo.',
    declineYes: 'Sí, rechazar',
    declineTalk: 'Hablar con {name}',
    payEyebrow: 'Pago',
    payTitle: 'Primer pago · {amount}',
    payLead: 'Tu cobertura empieza el día que se acredite este pago.',
    methodEyebrow: 'Forma de pago',
    methodTitle: 'Cambiar forma de pago',
    methodLead: 'Se usa para el débito mensual. Cambiar el medio no cambia la fecha de cobro.',
    payCard: 'Tarjeta',
    payAch: 'Cuenta bancaria',
    payCardNumber: 'Número de tarjeta',
    payExp: 'Vencimiento',
    payCvv: 'CVV',
    payRouting: 'Número de ruta (routing)',
    payAccount: 'Número de cuenta',
    payConsent: 'Autorizo el débito automático de {amount} el día 2 de cada mes, hasta que lo cancele.',
    payCta: 'Pagar {amount}',
    paySave: 'Guardar',
    payDoneTitle: 'Pago acreditado',
    payDoneLead: 'Tu póliza queda vigente desde hoy. Te mandamos el comprobante por correo.',
    methodDoneTitle: 'Forma de pago actualizada',
    methodDoneLead:
      'El próximo débito del 2 de octubre sale por el medio nuevo. Te avisamos por correo del cambio.',
    payDoneCta: 'Ver mi póliza',
    notifyEyebrow: 'Avisos',
    notifyTitle: 'Avisarte por WhatsApp',
    notifyLead: 'Te escribimos al {phone}, el número que ya tenemos registrado, cada vez que cambie el estado de tu solicitud.',
    notifyNoteTitle: 'Qué dice el mensaje',
    notifyNote:
      'Solo que hay novedades, con el enlace al portal. Nunca información médica ni la decisión, porque no sabemos quién más ve ese teléfono.',
    notifyLater: 'Ahora no',
    notifyActivate: 'Activar avisos',
    notifyDoneTitle: 'Avisos activados',
    notifyDoneLead: 'Puedes darte de baja respondiendo BAJA a cualquiera de esos mensajes.',

    toastReceived: 'Listo — el requisito quedó como recibido',
    toastSaved: 'Se guardó tu avance — puedes seguir cuando quieras',
    toastAttach: 'Abriría la cámara o el selector de archivos',
    toastAgentDemo: 'Demo — abriría WhatsApp con tu agente',
    toastDeclined: 'Solicitud cerrada — así la ve el cliente',
    toastResend: 'Llamaría a → POST /api/portal/:token/resend',

    protoLabel: 'Portal del Cliente · Prototipo',
    devMode: 'Modo desarrollo',
    specsTitle: 'Notas para desarrollo',
    specsLead:
      'Cada elemento interactivo: botones, campos y enlaces. Pasa el cursor por una tarjeta para ubicarlo en pantalla, o haz clic en un número para venir aquí.',
    specsOnLoad: 'Al cargar la pantalla',
    specsThisScreen: 'Esta pantalla',
    specsShared: 'Elementos compartidos',
    specsDoes: 'Qué hace',
    specsGoes: 'A dónde va',
    specsRules: 'Reglas',
    tabStart: 'Inicio',
    tabHelp: 'No puedo entrar',
  },
} as const;

export type ClientPortalCopyKey = keyof (typeof COPY)['es'];

export function clientPortalTraductor(
  language: ClientPortalLanguage | string | undefined,
  key: ClientPortalCopyKey,
  vars?: Record<string, string | number>,
): string {
  // en/pt fall back to es — no translation exists yet, see SPECS.lang.
  void language;
  const raw = COPY.es[key];
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name: string) =>
    vars[name] === undefined ? `{${name}}` : String(vars[name]),
  );
}
